import { useState } from "react";
import { useWebhookDeliveries, useRetryWebhookDelivery } from "@/hooks/useWebhooks";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

interface WebhookDeliveryLogProps {
  webhookId: string;
}

export function WebhookDeliveryLog({ webhookId }: WebhookDeliveryLogProps) {
  const { data: deliveries = [], isLoading, refetch } = useWebhookDeliveries(webhookId);
  const retryMutation = useRetryWebhookDelivery();
  const [expandedDeliveryId, setExpandedDeliveryId] = useState<string | null>(null);

  const handleRetry = async (deliveryId: string) => {
    try {
      await retryMutation.mutateAsync({ id: webhookId, deliveryId });
      toast.success("Retry request dispatched");
      refetch();
    } catch (err: any) {
      toast.error(err.message || "Failed to trigger retry");
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedDeliveryId(expandedDeliveryId === id ? null : id);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (deliveries.length === 0) {
    return (
      <div className="text-center py-8 border-2 border-dashed border-border text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
        No webhook deliveries logged yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center pb-2 border-b border-foreground/10">
        <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Recent Deliveries (Last 50)</span>
        <button
          onClick={() => refetch()}
          className="text-[9px] font-black uppercase hover:text-primary flex items-center gap-1"
        >
          <RefreshCw className="h-2.5 w-2.5" /> Refresh Log
        </button>
      </div>

      <div className="border-2 border-foreground divide-y-2 divide-foreground font-mono text-[9px]">
        {deliveries.map((d) => {
          const isSuccess = d.status === "delivered";
          const isFailed = d.status === "failed";
          const isRetrying = d.status === "retrying";
          const isExpanded = expandedDeliveryId === d.id;

          let statusBadge = "bg-yellow-500/20 text-yellow-600 border-yellow-500/50";
          if (isSuccess) statusBadge = "bg-status-up/10 text-status-up border-status-up/30";
          if (isFailed) statusBadge = "bg-status-down/10 text-status-down border-status-down/30";

          return (
            <div key={d.id} className="bg-background">
              {/* Row Summary */}
              <div
                onClick={() => toggleExpand(d.id)}
                className="flex flex-wrap items-center justify-between p-3 gap-2 cursor-pointer hover:bg-foreground/5 transition-colors select-none"
              >
                <div className="flex items-center gap-3">
                  <span className="font-bold text-foreground/50">
                    {new Date(d.created_at).toLocaleTimeString()}
                  </span>
                  <span className="font-bold text-[10px] text-foreground uppercase tracking-tight">
                    {d.event_type}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 border text-[8px] font-black uppercase ${statusBadge}`}>
                    {d.status}
                  </span>
                  {d.response_code && (
                    <span className="font-bold text-foreground">
                      HTTP {d.response_code}
                    </span>
                  )}
                  {d.latency_ms && (
                    <span className="text-muted-foreground">
                      {d.latency_ms}ms
                    </span>
                  )}
                  <span>
                    Try {d.attempts}/{d.max_attempts}
                  </span>
                  {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </div>
              </div>

              {/* Collapsible Details */}
              {isExpanded && (
                <div className="p-3 border-t-2 border-foreground bg-foreground/[0.02] space-y-3">
                  {d.error_message && (
                    <div className="p-2.5 bg-status-down/10 border border-status-down/30 text-status-down font-bold uppercase tracking-tight">
                      Error: {d.error_message}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[8px] font-black uppercase text-muted-foreground">Payload (JSON)</span>
                      <pre className="p-2 border border-foreground/10 bg-muted/20 overflow-x-auto text-[8px] leading-relaxed max-h-48">
                        {JSON.stringify(d.payload, null, 2)}
                      </pre>
                    </div>

                    <div className="space-y-2">
                      <div className="space-y-1">
                        <span className="text-[8px] font-black uppercase text-muted-foreground">Delivery Metadata</span>
                        <div className="p-2 border border-foreground/10 bg-muted/20 space-y-1">
                          <p><strong className="uppercase">Idempotency Key:</strong> {d.idempotency_key || "None"}</p>
                          {d.next_retry_at && (
                            <p className="text-yellow-600">
                              <strong className="uppercase">Next Retry:</strong> {new Date(d.next_retry_at).toLocaleString()}
                            </p>
                          )}
                          {d.delivered_at && (
                            <p className="text-status-up">
                              <strong className="uppercase">Delivered:</strong> {new Date(d.delivered_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Retry Button */}
                      {(isFailed || isRetrying) && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetry(d.id);
                          }}
                          disabled={retryMutation.isPending}
                          className="h-8 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase text-[9px] shadow-[2px_2px_0_0_#000] hover:bg-primary transition-all"
                        >
                          {retryMutation.isPending ? "Retrying..." : "Force Immediate Retry"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
